import * as React from 'react'

import { Box, Button, Tag, Title, useToggle } from '@factorialco/deprecated-design-system'
import copy from 'copy-to-clipboard'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { tagColor, tagLabel } from 'modules/surveys/lib/tagConstants'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import type Survey from 'modules/surveys/store/models/v2/Survey'
import { Status } from 'modules/surveys/store/models/v2/Survey'

import { factorialFormFromSurvey } from '../Settings/CycleConfiguration'
import RemindParticipantsModal from './RemindParticipantsModal'
import RestartSurveyModal from './RestartSurveyModal'
import StartSurveyModal from './StartSurveyModal'
import StopSurveyModal from './StopSurveyModal'

interface HeaderProps {
  survey: Survey
  canEditSurvey: boolean
}

function SurveyShowHeader({ survey, canEditSurvey }: HeaderProps) {
  const { canbeStarted } = useSurveyShowContext()
  const [startSurveyModalOpen, toggleStartSurveyModal] = useToggle(false)
  const [stopSurveyModalOpen, toggleStopSurveyModal] = useToggle(false)
  const [restartSurveyModalOpen, toggleRestartSurveyModal] = useToggle(false)
  const [remindParticipantsModalOpen, toggleRemindParticipantsModal] = useToggle(false)
  const [startingWorkflowSurvey, setStartingWorkflowSurvey] = React.useState(false)

  const copySurveyLinkURL = () => {
    copy(
      `${window.location.origin}${navigation.engagement.surveys.id.answer.fullPath({
        id: survey.id,
      })}`
    )
    app.setFlash({
      type: 'correct',
      message: i18n.t('common.copied'),
    })
  }
  const surveyStatus = survey.attributes.status
  const { navigation } = useNavigation()
  const name = survey ? survey.attributes.name : i18n.t('surveys.this_form')

  const startSurvey = async () => {
    if (survey.isWorkflowSurvey) {
      setStartingWorkflowSurvey(true)

      await survey.startSurvey()

      survey.fetch().then(() => {
        setStartingWorkflowSurvey(false)
      })
    } else {
      toggleStartSurveyModal(true)()
    }
  }

  const action = () => {
    switch (surveyStatus) {
      case Status.active:
        if (survey.isWorkflowSurvey) return undefined

        return {
          icon: 'stop',
          label: i18n.t('surveys.form.stop.action'),
          onClick: toggleStopSurveyModal(true),
        } as const

      case Status.scheduled:
        return {
          icon: 'stop',
          label: i18n.t('surveys.form.stop.action'),
          onClick: toggleStopSurveyModal(true), // TODO: review the modal
        } as const

      case Status.draft:
        return {
          icon: 'play',
          label: i18n.t('surveys.form.start.action'),
          onClick: async () => await startSurvey(),
          disabled: !canbeStarted || startingWorkflowSurvey,
        } as const

      case Status.finished:
        return {
          icon: 'play',
          label: i18n.t('surveys.form.restart.action'),
          onClick: toggleRestartSurveyModal(true),
          disabled: !canbeStarted,
        } as const

      default:
        return undefined
    }
  }

  if (!canEditSurvey) {
    return null
  }

  const primaryAction = action()

  return (
    <>
      <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
        <Box gap="s16">
          <Tag type={tagColor(survey.attributes.status)} inline>
            {tagLabel(survey.attributes.status)}
          </Tag>
          <Title title={name} />
        </Box>

        <Box flexDirection="row" gap="s16" alignItems="center">
          {primaryAction ? (
            <Button
              onClick={primaryAction.onClick}
              iconName={primaryAction.icon}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </Button>
          ) : null}

          {surveyStatus === 'active' || surveyStatus === 'scheduled' ? (
            <PopoverMenu type="list">
              <PopoverMenu.Trigger asChild>
                <Button type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                {surveyStatus === 'active' && (
                  <PopoverMenu.TouchableOption
                    title={i18n.t('surveys.link')}
                    onClick={copySurveyLinkURL}
                  />
                )}

                {surveyStatus === 'active' && (
                  <PopoverMenu.TouchableOption
                    title={i18n.t('surveys.remind_participants')}
                    onClick={toggleRemindParticipantsModal(true)}
                  />
                )}

                {surveyStatus === 'scheduled' && (
                  <PopoverMenu.TouchableOption
                    title={i18n.t('surveys.link')}
                    onClick={copySurveyLinkURL}
                  />
                )}
              </PopoverMenu.Content>
            </PopoverMenu>
          ) : null}
        </Box>
      </Box>

      {remindParticipantsModalOpen && (
        <RemindParticipantsModal survey={survey} onClose={toggleRemindParticipantsModal(false)} />
      )}

      {survey && startSurveyModalOpen && (
        <StartSurveyModal
          survey={survey}
          onClose={toggleStartSurveyModal(false)}
          surveyData={factorialFormFromSurvey(survey)}
        />
      )}

      {stopSurveyModalOpen && (
        <StopSurveyModal survey={survey} onClose={toggleStopSurveyModal(false)} />
      )}

      {restartSurveyModalOpen && (
        <RestartSurveyModal
          survey={survey}
          onClose={toggleRestartSurveyModal(false)}
          surveyData={factorialFormFromSurvey(survey)}
        />
      )}
    </>
  )
}

export default observer(SurveyShowHeader)
